import { useMemo } from 'react';
import { cn } from '../../lib/utils';
import { usePlatforms } from '../../hooks';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const barColors = ['#721b1f', '#30abe8', '#22C35D', '#b152e0', '#f59f0a', '#818898'];

export function PlatformBreakdown({ enabled = true }) {
  const { data: platformsData, isPending } = usePlatforms({ enabled });

  const { totalRequests, totalRequestsOthers } = useMemo(() => {
    const platforms = platformsData?.platforms || [];
    const total = platforms.reduce((sum, platform) => sum + (platform?.count || 0), 0);
    const others = platforms.slice(5).reduce((sum, platform) => sum + (platform?.count || 0), 0);
    return { totalRequests: total, totalRequestsOthers: others };
  }, [platformsData?.platforms]);

  return (
    <div className="p-6 rounded-xl bg-white border border-shell-border">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-sm font-semibold text-ink">Platform Breakdown</h3>
          <p className="text-xs text-mute mt-0.5">Requests by client application</p>
        </div>
        <span className="text-xs text-mute">{platformsData?.platforms?.length ?? 0} platforms</span>
      </div>

      {isPending ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} height={20} borderRadius={6} baseColor="#262626" highlightColor="#333" />
          ))}
        </div>
      ) : (
        <>
          <div className="flex h-3 rounded-full overflow-hidden mb-5 gap-0.5">
            {platformsData?.platforms?.slice(0, 5)?.map((p, i) => (
              <div
                key={p.platform_id || i}
                className="h-full transition-all"
                style={{
                  width: totalRequests ? `${(p.count / totalRequests) * 100}%` : '0%',
                  backgroundColor: barColors[i],
                }}
              />
            ))}
            <div
              className="h-full transition-all"
              style={{
                width: totalRequests ? `${(totalRequestsOthers / totalRequests) * 100}%` : '0%',
                backgroundColor: '#818898',
              }}
            />
          </div>

          <div className="space-y-3">
            {platformsData?.platforms?.slice(0, 5)?.map((platform, i) => (
              <div
                key={platform?.platform_id}
                className="flex items-center justify-between py-2 border-b border-shell-border">
                <p className="text-sm font-medium text-ink capitalize mb-0">{platform?.platform_id}</p>
                <div className="flex items-center gap-4">
                  <p className="text-sm font-medium text-ink tabular-nums mb-0">{platform?.count}</p>
                  <p className="text-xs text-mute mb-0">requests</p>
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: barColors[i] }} />
                </div>
              </div>
            ))}
            <div className="flex items-center justify-between py-2">
              <p className="text-sm font-medium text-ink capitalize mb-0">Others</p>
              <div className="flex items-center gap-4">
                <p className="text-sm font-medium text-ink tabular-nums mb-0">{totalRequestsOthers}</p>
                <p className="text-xs text-mute mb-0">requests</p>
                <div className={cn('w-2 h-2 rounded-full bg-[#818898]')} />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
